import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'

import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import {
  EnabledKey,
  FeatureGroup,
  PlanName
} from '@/features/Pricing/pricing-plans'
import {
  CheckIconGreen,
  DownArrowWhiteIcon,
  EmptyLineIcon,
  InfoCircleGrey
} from '@/components/icons'
import Tooltip from '@/components/popper-tooltip'
import { Label, ParagraphMedium } from '@/components/text'

import StatusBadge from '../status-badge'

const TableWrapper = styled.div`
  position: relative;
  --card-gap: 16px;
  --header-height: 56px;
  padding: var(--card-gap);
  margin: 0 0 16px;
`

const Table = styled.table`
  position: relative;
  border-collapse: collapse;
  display: grid;
  min-width: 100%;
  grid-template-columns: 1fr repeat(3, 196px);
  @media (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const item = css``

const contents = css`
  display: contents;
`

const Heading = styled.th<{ closed?: boolean }>`
  ${item}
  height: var(--header-height);
  padding: 16px;
  text-align: left;
  font-weight: normal;
  font-size: 16px;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    grid-column: 1 / 3 span;
    justify-content: space-between;
    padding: 0 8px;
    height: 32px;
    ${props =>
      props.closed &&
      `
      margin-bottom: 0 !important;
   `}
  }
`

const PlanNameHeading = styled(Heading)`
  justify-content: center;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    display: none;
  }
`

const Item = styled.td`
  ${item}
  grid-gap: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 60px;
  padding: 12px 16px;
  color: #808080;
  background: ${colors.grey96};
  justify-content: center;
  &:not(:first-of-type):not(:last-of-type) {
    border-right: 2px solid ${colors.white};
  }
  @media (max-width: ${breakpoints.tabletLandscape}) {
    &:not(:first-of-type):not(:last-of-type) {
      border: none;
    }
    &:first-of-type {
      height: 48px;
    }
    &:not(:first-of-type) {
      height: 60px;
    }
    align-items: flex-start;
    padding: 12px 20px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 12px;
  }
`

const FeatureItem = styled(Item)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 14px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    grid-column: 1 / 3 span;
    color: ${colors.regular};
  }
`

const Row = styled.tr`
  ${contents}
  &:nth-child(even) td {
    background: ${colors.white};
  }
  td:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  td:last-of-type {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  @media (max-width: ${breakpoints.tabletLandscape}) {
    td:first-of-type {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 0;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 0;
    }
    td:nth-of-type(2) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 8px;
    }
    td:last-of-type {
      border-top-right-radius: 0;
      border-bottom-right-radius: 8px;
    }
    td:not(:first-of-type) {
      padding-top: 0;
    }
  }
`

const HeaderRow = styled(Row)`
  > th {
    margin-bottom: var(--card-gap);
  }
`

const Head = styled.thead`
  ${contents}
`

const Body = styled.tbody`
  ${contents}
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  position: relative;
  &:after {
    content: '';
    width: calc(100% + var(--card-gap) * 2);
    height: calc(100% - var(--header-height) + var(--card-gap));
    position: absolute;
    top: var(--header-height);
    left: calc(-1 * var(--card-gap));
    box-shadow: 0px 1px 3px rgb(0 0 0 / 10%), 0px 1px 2px rgb(0 0 0 / 6%);
    border-radius: 8px;
    pointer-events: none;
    @media (max-width: ${breakpoints.tabletLandscape}) {
      height: calc(100% + var(--card-gap) * 2);
      top: calc(-1 * var(--card-gap));
    }
  }
`

const MobileLabel = styled(Label)`
  display: none;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    display: block;
  }
`

const IconContianer = styled.div`
  display: flex;
  align-items: center;
  max-height: 20px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    height: 20px;
  }
`

const Arrow = styled(DownArrowWhiteIcon)<{ active?: boolean }>`
  transition: transform 0.3s ease-in-out;
  transform: rotate(0deg);
  path {
    fill: #494949;
  }

  ${props =>
    props.active &&
    `
    transform: rotate(180deg);
    path {
      fill: ${colors.primary} !important;
    }
  `}
`

const Button = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border-radius: 8px;
  height: 64px;
  z-index: 10;
  background: none;
`

const FeatureNameText: React.FC<
  React.ComponentProps<typeof ParagraphMedium>
> = props => {
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  if (isTablet) return <Label type="large" fontSize="14px" {...props} />
  return <ParagraphMedium {...props} />
}

const HeaderText: React.FC<
  React.ComponentProps<typeof ParagraphMedium>
> = props => {
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  if (isTablet) return <Label type="large" {...props} />
  return <Label type="medium" {...props} />
}

const FeatureText: React.FC<
  React.ComponentProps<typeof ParagraphMedium>
> = props => {
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  if (isTablet) return <Label type="small" fontSize="14px" {...props} />
  return <ParagraphMedium {...props} />
}

const FeatureTextColor = styled(FeatureText)`
  @media (max-width: ${breakpoints.tabletLandscape}) {
    color: ${colors.grey16};
  }
`

const PerMonthWrapper = styled.div`
  @media (max-width: ${breakpoints.phoneMax}) {
    min-height: 28px;
  }
`

type PlanNameObj = {
  key: PlanName
  value: React.ReactNode
}

type FeatureMatrixProps = {
  feature: FeatureGroup
  plans: PlanNameObj[]
}

type FeatureIconProps = {
  included: boolean
  children?: React.ReactNode
}

const FeatureIcon: React.FC<FeatureIconProps> = ({ included }) => {
  const { t } = useTranslation('pricing')
  return (
    <IconContianer>
      {included ? (
        <CheckIconGreen
          width={20}
          height={20}
          aria-label={t('pricing:Included')}
        />
      ) : (
        <EmptyLineIcon
          width={12}
          height={2}
          aria-label={t('pricing:Not included')}
        />
      )}
    </IconContianer>
  )
}

const isFreeText = (item: EnabledKey): item is React.ReactNode =>
  typeof item !== 'boolean' && typeof item !== 'undefined'

const getFeatureText = (item: EnabledKey): React.ReactNode => {
  if (!isFreeText(item)) return <FeatureIcon included={item} />
  return (
    <PerMonthWrapper>
      <FeatureTextColor>{item}</FeatureTextColor>
    </PerMonthWrapper>
  )
}

export const FeatureMatrix: React.FC<FeatureMatrixProps> = ({
  feature,
  plans
}) => {
  const { title, features, key: featureKey } = feature
  const [open, setOpen] = useState(false)
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const showContent = open || !isTablet
  const onClick = () => setOpen(value => !value)
  return (
    <TableWrapper>
      {isTablet && (
        <Button
          aria-controls={`feature-${featureKey}`}
          aria-expanded={showContent}
          aria-label={title}
          onClick={onClick}
        />
      )}
      <Table id={`feature-${featureKey}`}>
        <Head>
          <HeaderRow>
            <Heading closed={!showContent}>
              <HeaderText>{title}</HeaderText>
              {isTablet && (
                <Arrow
                  active={showContent}
                  width={15}
                  height={12}
                  aria-hidden
                />
              )}
            </Heading>
            {plans.map(({ key, value }) => (
              <PlanNameHeading key={key}>
                <Label type="large">{value}</Label>
              </PlanNameHeading>
            ))}
          </HeaderRow>
        </Head>
        {showContent ? (
          <Body>
            {features.map(
              ({ name, key: nameKey, tooltip, status, ...feature }) => (
                <Row key={`feature-${featureKey}-${nameKey}`}>
                  <FeatureItem>
                    <Tooltip title={name} content={tooltip}>
                      <FeatureNameText as="span">{name}</FeatureNameText>
                      <InfoCircleGrey width={18} height={18} />
                    </Tooltip>
                    {status && <StatusBadge title={status} type="warning" />}
                  </FeatureItem>
                  {plans.map(({ key, value }) => (
                    <Item key={`feature-${featureKey}-${nameKey}-${key}`}>
                      {getFeatureText(feature[key])}
                      <MobileLabel type="medium" fontSize="14px">
                        {value}
                      </MobileLabel>
                    </Item>
                  ))}
                </Row>
              )
            )}
          </Body>
        ) : (
          <Body></Body>
        )}
      </Table>
    </TableWrapper>
  )
}
